import type { CreateSubscriptionInput, ResumeSubscriptionInput, SubStatus, UpdateSubscriptionInput } from '@financiar/shared';
import { apiClient } from '../../api/client';
import type {
  GenerationLogRow,
  Paginated,
  SubscriptionDetail,
  SubscriptionGenerateNowResult,
  SubscriptionListItem,
} from '../../api/types';

/** Everything `GET /subscriptions` accepts (`listSubscriptionsQuerySchema`). */
export interface ListSubscriptionsParams {
  companyId: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: SubStatus;
  clientId?: string;
}

/** The list page plus the filtered total value (summed across ALL pages) for
 * the table's total row — same shape as the invoices list's `FacturiPage`. */
export interface AbonamentePage extends Paginated<SubscriptionListItem> {
  totals: { monthlyValue: string };
}

export async function fetchAbonamente(params: ListSubscriptionsParams): Promise<AbonamentePage> {
  const { data } = await apiClient.get<AbonamentePage>('/subscriptions', { params });
  return data;
}

export async function fetchAbonament(id: string): Promise<SubscriptionDetail> {
  const { data } = await apiClient.get<SubscriptionDetail>(`/subscriptions/${id}`);
  return data;
}

export async function createAbonament(input: CreateSubscriptionInput): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post<SubscriptionDetail>('/subscriptions', input);
  return data;
}

export async function updateAbonament(id: string, input: UpdateSubscriptionInput): Promise<SubscriptionDetail> {
  const { data } = await apiClient.patch<SubscriptionDetail>(`/subscriptions/${id}`, input);
  return data;
}

export async function deleteAbonament(id: string): Promise<void> {
  await apiClient.delete(`/subscriptions/${id}`);
}

export async function pauseAbonament(id: string): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post<SubscriptionDetail>(`/subscriptions/${id}/pause`, {});
  return data;
}

export async function resumeAbonament(id: string, input: ResumeSubscriptionInput): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post<SubscriptionDetail>(`/subscriptions/${id}/resume`, input);
  return data;
}

/** The cursor's period is generated exactly once and the cursor advances —
 * see the "Generează acum" confirmation copy in AbonamenteListPage. */
export async function generateNowAbonament(id: string): Promise<SubscriptionGenerateNowResult> {
  const { data } = await apiClient.post<SubscriptionGenerateNowResult>(`/subscriptions/${id}/generate-now`, {});
  return data;
}

/** Everything `GET /generation-log` accepts (`listGenerationLogQuerySchema`)
 * — a TOP-LEVEL route, not nested under `/subscriptions` (see
 * `generation-log.controller.ts`'s own doc comment on the API side). */
export interface ListGenerationLogParams {
  page: number;
  pageSize: number;
  subscriptionId?: string;
  status?: 'SUCCESS' | 'FAILED';
}

export async function fetchGenerationLog(params: ListGenerationLogParams): Promise<Paginated<GenerationLogRow>> {
  const { data } = await apiClient.get<Paginated<GenerationLogRow>>('/generation-log', { params });
  return data;
}
